import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SwalAlert } from '@/hooks/useSweetAlert';
import { socialAccounts as initialAccounts, statusMeta } from '@/mocks/socialConnect';
import type { SocialAccount, SocialPlatform, ConnectionStatus } from '@/mocks/socialConnect';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-GT', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatNumber(n: number): string {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toString();
}

export default function AdminSocialConnect() {
  const [accounts, setAccounts] = useState<SocialAccount[]>(initialAccounts);
  const [connecting, setConnecting] = useState<Record<string, boolean>>({});

  const connectedCount = accounts.filter((a) => a.status === 'conectado').length;
  const totalCount = accounts.length;

  /* ── Conectar ── */
  const handleConnect = (platform: SocialPlatform) => {
    setConnecting((prev) => ({ ...prev, [platform]: true }));

    // Simular OAuth flow
    setTimeout(() => {
      setAccounts((prev) =>
        prev.map((a) =>
          a.id === platform
            ? {
                ...a,
                status: 'conectado' as ConnectionStatus,
                connectedAt: new Date().toISOString(),
                lastSync: new Date().toISOString(),
                accountName:
                  platform === 'facebook'
                    ? 'Radio RFW Oficial'
                    : platform === 'youtube'
                      ? 'RadioFrecuenciaWeb TV'
                      : platform === 'instagram'
                        ? '@radiofrecuenciaweb'
                        : '@radiofrecuenciaweb',
                accountId: `PF-${Date.now()}`,
                pageUrl:
                  platform === 'facebook'
                    ? 'https://facebook.com/RadioFrecuenciaWeb'
                    : platform === 'youtube'
                      ? 'https://youtube.com/@RadioFrecuenciaWebTV'
                      : platform === 'instagram'
                        ? 'https://instagram.com/radiofrecuenciaweb'
                        : 'https://tiktok.com/@radiofrecuenciaweb',
                followers: Math.floor(Math.random() * 30000) + 5000,
                errorMessage: null,
              }
            : a,
        ),
      );
      setConnecting((prev) => ({ ...prev, [platform]: false }));
      SwalAlert.toast(`${accounts.find((a) => a.id === platform)?.name || ''} conectado`, 'success');
    }, 1800);
  };

  /* ── Reconectar ── */
  const handleReconnect = (platform: SocialPlatform) => {
    setConnecting((prev) => ({ ...prev, [platform]: true }));

    setTimeout(() => {
      setAccounts((prev) =>
        prev.map((a) =>
          a.id === platform
            ? {
                ...a,
                status: 'conectado' as ConnectionStatus,
                lastSync: new Date().toISOString(),
                errorMessage: null,
              }
            : a,
        ),
      );
      setConnecting((prev) => ({ ...prev, [platform]: false }));
      SwalAlert.toast(`${accounts.find((a) => a.id === platform)?.name || ''} reconectado`, 'success');
    }, 1500);
  };

  /* ── Desconectar ── */
  const handleDisconnect = (platform: SocialPlatform) => {
    const account = accounts.find((a) => a.id === platform);
    SwalAlert.confirm(
      `¿Desconectar ${account?.name}?`,
      `Se eliminará la conexión con ${account?.accountName || account?.name}. Las publicaciones automáticas se detendrán. Podés volver a conectar cuando quieras.`,
    ).then((result) => {
      if (result.isConfirmed) {
        setAccounts((prev) =>
          prev.map((a) =>
            a.id === platform
              ? {
                  ...a,
                  status: 'pendiente' as ConnectionStatus,
                  accountName: '',
                  accountId: '',
                  pageUrl: '',
                  connectedAt: null,
                  followers: 0,
                  lastSync: null,
                  errorMessage: null,
                }
              : a,
          ),
        );
        SwalAlert.toast(`${account?.name} desconectado`, 'info');
      }
    });
  };

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5 pl-12">
            <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-share-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Social Connectors</h1>
              <p className="text-foreground-400 text-[9px]">{connectedCount}/{totalCount} conectadas</p>
            </div>
          </div>
          <Link to="/admin/dashboard" className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors">
            <i className="ri-arrow-left-line text-xs"></i>Volver
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div className="flex items-center gap-4">
              <div className="w-9 h-9 rounded-xl bg-accent-100 flex items-center justify-center">
                <i className="ri-share-line text-base text-accent-600"></i>
              </div>
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Social Connectors</h2>
                <p className="text-foreground-400 text-xs">
                  {connectedCount} de {totalCount} plataformas conectadas
                </p>
              </div>
            </div>
            {/* Connection count pill */}
            <div className="flex items-center gap-2">
              {(['conectado', 'pendiente', 'error'] as ConnectionStatus[]).map((s) => {
                const count = accounts.filter((a) => a.status === s).length;
                if (count === 0) return null;
                const meta = statusMeta[s];
                return (
                  <span key={s} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold ${meta.bg} ${meta.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>
                    {count} {meta.label}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6 lg:p-8 pb-8">
            <div className="max-w-[1000px] mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {accounts.map((account) => {
                  const meta = statusMeta[account.status];
                  const isConnecting = connecting[account.id] || false;
                  const isConnected = account.status === 'conectado';
                  const isError = account.status === 'error';
                  const isPending = account.status === 'pendiente';

                  return (
                    <div
                      key={account.id}
                      className={`bg-background-50 rounded-2xl border-2 transition-colors ${
                        isConnected ? 'border-emerald-200' : isError ? 'border-rose-200' : 'border-background-200 hover:border-background-300'
                      }`}
                    >
                      <div className="p-5">
                        {/* Header: icon + name + status */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-11 h-11 rounded-xl flex items-center justify-center ${account.bgColor}`}
                            >
                              <i className={`${account.icon} text-background-50 text-lg`}></i>
                            </div>
                            <div>
                              <h3 className="text-foreground-950 font-bold text-sm">{account.name}</h3>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold ${meta.bg} ${meta.text}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot} ${isConnected ? 'animate-pulse' : ''}`}></span>
                                {meta.label}
                              </span>
                            </div>
                          </div>
                          <span className="text-foreground-400 text-[10px]">{account.label}</span>
                        </div>

                        {/* Account details (when connected or error) */}
                        {(isConnected || isError) && (
                          <div className="bg-background-100 rounded-xl p-3 mb-4 space-y-2">
                            {account.accountName && (
                              <div className="flex items-center justify-between text-[10px]">
                                <span className="text-foreground-400">{account.id === 'facebook' ? 'Página' : account.id === 'youtube' ? 'Canal' : 'Cuenta'}</span>
                                <span className="text-foreground-700 font-semibold text-right truncate ml-4 max-w-[200px]">{account.accountName}</span>
                              </div>
                            )}
                            {account.followers > 0 && (
                              <div className="flex items-center justify-between text-[10px]">
                                <span className="text-foreground-400">Seguidores</span>
                                <span className="text-foreground-700 font-semibold">{formatNumber(account.followers)}</span>
                              </div>
                            )}
                            {account.connectedAt && (
                              <div className="flex items-center justify-between text-[10px]">
                                <span className="text-foreground-400">Conectado desde</span>
                                <span className="text-foreground-700 font-semibold">{formatDate(account.connectedAt)}</span>
                              </div>
                            )}
                            {account.lastSync && (
                              <div className="flex items-center justify-between text-[10px]">
                                <span className="text-foreground-400">Última sincronización</span>
                                <span className="text-foreground-700 font-semibold">{formatDate(account.lastSync)}</span>
                              </div>
                            )}
                            {isError && account.errorMessage && (
                              <div className="flex items-start gap-1.5 pt-2 border-t border-rose-200">
                                <i className="ri-error-warning-line text-rose-500 text-[10px] shrink-0 mt-0.5"></i>
                                <p className="text-rose-600 text-[10px] leading-relaxed">{account.errorMessage}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Pending state message */}
                        {isPending && (
                          <div className="bg-amber-50 rounded-xl p-3 mb-4 border border-amber-100">
                            <p className="text-amber-700 text-[10px] leading-relaxed">
                              Esta cuenta aún no está conectada. Vincular {account.name} te permite publicar contenido y notificaciones automáticas cuando iniciás una transmisión.
                            </p>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {isPending && (
                            <button
                              onClick={() => handleConnect(account.id)}
                              disabled={isConnecting}
                              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-colors cursor-pointer whitespace-nowrap ${
                                isConnecting
                                  ? 'bg-background-100 text-foreground-300 cursor-not-allowed'
                                  : `text-background-50 ${account.id === 'facebook' ? 'bg-[#1877F2] hover:bg-[#1666d6]' : account.id === 'youtube' ? 'bg-[#FF0000] hover:bg-[#d90000]' : account.id === 'instagram' ? 'bg-[#E4405F] hover:bg-[#c9354f]' : 'bg-[#000000] hover:bg-[#1a1a1a]'}`
                              }`}
                            >
                              <i className={`text-xs ${isConnecting ? 'ri-loader-4-line animate-spin' : 'ri-link'}`}></i>
                              {isConnecting ? 'Conectando...' : 'Conectar'}
                            </button>
                          )}

                          {(isConnected || isError) && (
                            <>
                              <button
                                onClick={() => handleReconnect(account.id)}
                                disabled={isConnecting}
                                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-colors cursor-pointer whitespace-nowrap ${
                                  isConnecting
                                    ? 'bg-background-100 text-foreground-300 cursor-not-allowed'
                                    : 'bg-accent-500 text-background-50 hover:bg-accent-600'
                                }`}
                              >
                                <i className={`text-xs ${isConnecting ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'}`}></i>
                                {isConnecting ? 'Reconectando...' : 'Reconectar'}
                              </button>
                              <button
                                onClick={() => handleDisconnect(account.id)}
                                disabled={isConnecting}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-background-100 text-foreground-500 hover:bg-rose-50 hover:text-rose-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                              >
                                <i className="ri-link-unlink text-xs"></i>
                                Desconectar
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </main>
      </div>


    </div>
  );
}